import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { type ForYouMatch, forYou } from "@/lib/discover-for-you";
import { useMe } from "@/lib/hooks/use-me";
import { queryOptions_projects_employee_day_range } from "@/lib/requests/project/project.employee-day-range";
import { startOfDayInTz } from "@/utils/format-datetime";

const DAY = 24 * 60 * 60 * 1000;

/**
 * Zes weken vooruit — hetzelfde venster als het herziene Ontdek.
 *
 * Een nudge gaat over "hier kun je nú op reageren". Werk in maart is geen aanleiding om vandaag
 * iets te doen, en het maakt de kans groot dat dezelfde suggestie weken achter elkaar terugkomt.
 */
const WEEKS_AHEAD = 6;

/** Drie past onder je diensten zonder het scherm over te nemen. Ontdek zelf toont er vijf. */
export const NUDGE_LIMIT = 3;

const TAKE = 200;

/**
 * Werk dat bij deze medewerker past en waarop hij zich vandaag nog kan aanmelden.
 *
 * Leunt op dezelfde `forYou()` als Ontdek — twee harde regels, jouw functie of jouw plaats — zodat
 * beide schermen dezelfde belofte doen. Eén verschil: hier filteren we eerst op `canApply`. Op
 * Ontdek is een rij zonder aanmeldknop nog steeds informatie; in een nudge die zegt "meld je aan"
 * is het een leugen.
 */
export function useForYouNudge({ enabled = true }: { enabled?: boolean } = {}) {
  const { me } = useMe();

  // Afgeleid van het begin van de dag in projecttijd, zodat de querysleutel binnen een dag niet
  // verschuift en elke render dezelfde cache raakt. Tempo's `dayStart()` deed dat op twee manieren
  // niet: het nulde alleen uur/minuut/seconde, dus de milliseconden bleven staan en de sleutel
  // verschoof alsnog bij elke render — en het rekende in de klok van het toestel.
  const startOfToday = startOfDayInTz();
  const from = startOfToday.toISOString();
  const to = new Date(startOfToday.getTime() + WEEKS_AHEAD * 7 * DAY).toISOString();

  const cards = useQuery({
    ...queryOptions_projects_employee_day_range({
      filter: [
        {
          AND: [
            { startDate: { operator: "gte" as const, value: from } },
            { startDate: { operator: "lte" as const, value: to } },
          ],
        },
      ],
      sort: { field: "startDate", order: "asc" },
      pagination: { take: TAKE, page: 1 },
    }),
    enabled,
  });

  const myFunctions = useQuery({
    queryKey: ["userFunction", "my-functions"],
    queryFn: () => extractionWrapper(api.userFunction.getMyFunctions()),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  const matches: ForYouMatch[] = useMemo(
    () =>
      forYou(
        (cards.data?.items ?? []).filter((card) => card.canApply),
        {
          myFunctions: (myFunctions.data ?? []).map((fn) => fn.name),
          myCity: me?.employee?.city,
        },
        NUDGE_LIMIT,
      ),
    [cards.data?.items, myFunctions.data, me?.employee?.city],
  );

  const refetch = async () => {
    await Promise.all([cards.refetch(), myFunctions.refetch()]);
  };

  return { matches, refetch };
}
